import { LanguagesEnum } from '@/api/enums';
import { handleAxiosError } from '@/api/helpers';
import {
  InteractiveElementType,
  Slide,
  SlideTemplateType,
  SlideType,
  TextAlignment,
} from '@/client/courses';
import { ContentEmailComponent } from '@/components/courses/elements/ContentEmailComponent';
import { ContentSMSComponent } from '@/components/courses/elements/ContentSMSComponent';
import { SlideImageComponent } from '@/components/slides/elements/SlideImageComponent';
import { SlidesNavigation } from '@/components/slides/SlidesNavigation';
import { useSlideDeck } from '@/hooks/query';
import { useAccountVariables } from '@/hooks/query/account-variables.hooks';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useToast } from '@/hooks/useToast';
import { useTransitionFocus } from '@/hooks/useTransitionFocus';
import { selectCurrentAccount } from '@/store/features/account';
import { ContentColumn } from '@/ui/content-column/ContentColumn';
import { InfoBox } from '@/ui/info-box';
import { ProgressHeader } from '@/ui/progress-header/ProgressHeader';
import { RichTextRenderer } from '@/ui/rich-text-renderer';
import { FlexContainer } from '@/ui/styled-ui';
import { withExamplePointOfContact } from '@/utils/helpers/point-of-contact.helper';
import { isMobile } from '@/utils/helpers/ui.helper';
import { PostHogFeatureFlags } from '@/utils/posthog/posthog.events';
import { AxiosError } from 'axios';
import { ProgressSpinner } from 'primereact/progressspinner';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

const getPopupSlideId = (slide: Slide): string | undefined => {
  if (slide.popupSlide) {
    return typeof slide.popupSlide === 'string'
      ? slide.popupSlide
      : slide.popupSlide.id;
  }
  return slide.tempPopupSlideId;
};

const getPopupSlideContent = (
  slide: Slide,
  allSlides: Slide[],
): Slide | undefined => {
  if (!slide.popupSlide) return undefined;

  const popupId = getPopupSlideId(slide);
  if (!popupId) return undefined;

  const linkedSlide = allSlides.find(({ id }) => id === popupId);

  return linkedSlide;
};

const ViewerContainer = styled.div<{
  isMobile: boolean;
  isPurpleBackground?: boolean;
}>`
  background: ${({ isPurpleBackground }) =>
    isPurpleBackground ? 'var(--purple-dark)' : 'var(--beige-main)'};
  color: ${({ isPurpleBackground }) =>
    isPurpleBackground ? 'var(--white-main)' : 'var(--black-main)'};
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  border-radius: var(--default-border-radius);
  position: relative;
  ${({ isMobile }) => isMobile && 'padding:  var(--small-padding);'}
`;

const ScrollShadowWrapper = styled.div`
  flex: 1;
  min-height: 0;
  position: relative;
  width: 100%;
`;

const ScrollShadow = styled.div<{
  $position: 'top' | 'bottom';
  $visible: boolean;
}>`
  position: absolute;
  left: 0;
  right: 0;
  height: 12px;
  pointer-events: none;
  z-index: 2;
  transition: opacity 0.2s ease;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  ${({ $position }) =>
    $position === 'top'
      ? `
    top: 0;
    background: linear-gradient(to bottom, rgba(0, 0, 0, 0.06), transparent);
  `
      : `
    bottom: 0;
    background: linear-gradient(to top, rgba(0, 0, 0, 0.06), transparent);
  `}
`;

const MainContent = styled.div<{ isPurpleBackground?: boolean }>`
  overflow-x: hidden;
  overflow-y: auto;
  height: 100%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  width: 100%;
  max-width: 1000px;
  min-height: 0;
  scroll-padding-bottom: 100px;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE / legacy Edge */
  &::-webkit-scrollbar {
    /* Chrome / Safari / WebKit */
    display: none;
  }

  h1 {
    margin-bottom: var(--medium-padding);
    font-size: 30px;
    font-weight: 700;
    color: ${({ isPurpleBackground }) =>
      isPurpleBackground ? 'var(--white-main)' : 'var(--black-main)'};
  }

  .slide-content {
    font-size: var(--default-font-size);
    line-height: 1.6;
    color: var(--black-main);
  }
`;

const SlidesNavigationContainer = styled.div<{
  isPurpleBackground?: boolean;
}>`
  padding: var(--default-padding-small);
  border-top: 1px solid
    ${({ isPurpleBackground }) =>
      isPurpleBackground ? 'var(--purple-dark)' : 'var(--gray-dark)'};
  position: sticky;
  bottom: 0;
  background-color: ${({ isPurpleBackground }) =>
    isPurpleBackground ? 'var(--purple-dark)' : 'var(--beige-main)'};
  z-index: 1000;
  border-radius: 0 0 var(--default-border-radius) var(--default-border-radius);
`;

const Footnote = styled.p`
  font-size: var(--xsmall-font-size);
  margin-top: var(--medium-padding);
  width: 100%;
  white-space: pre-wrap;
`;

const SlideContentContainer = styled(FlexContainer)<{
  isMobile: boolean;
  textAlignment: TextAlignment;
}>`
  min-height: 0;
  align-items: flex-start;
  overflow: visible;
  ${({ textAlignment }) =>
    textAlignment === TextAlignment.CENTER &&
    `
    text-align: center;
  `}
  ${({ textAlignment }) =>
    textAlignment === TextAlignment.RIGHT &&
    `
    text-align: right;
  `}

  ${({ isMobile }) =>
    isMobile &&
    `
    @media (orientation: portrait) {
      flex-direction: column-reverse;
      gap: 16px;
    }
  `}
`;

const ImageGridContainer = styled.div<{
  textAlignment: TextAlignment;
  isGallery?: boolean;
  isChapter?: boolean;
  isMobile?: boolean;
}>`
  display: grid;
  grid-auto-flow: ${({ isMobile }) => (isMobile ? 'row' : 'column')};
  grid-template-rows: auto;
  grid-auto-columns: ${({ isGallery }) =>
    isGallery ? 'minmax(0, 1fr)' : '1fr'};
  ${({ isMobile }) =>
    isMobile &&
    `
    grid-template-columns: 1fr;
    grid-auto-rows: auto;
  `}
  gap: var(--small-padding);
  width: 100%;
  max-width: ${({ isGallery, isMobile }) =>
    isGallery ? '100%' : isMobile ? '100%' : '375px'};
  min-width: 0;
  max-height: 100%;
  box-sizing: border-box;
  align-items: start;
  justify-items: ${({ textAlignment }) =>
    textAlignment === TextAlignment.CENTER
      ? 'center'
      : textAlignment === TextAlignment.RIGHT
      ? 'end'
      : 'start'};
  justify-content: ${({ textAlignment }) =>
    textAlignment === TextAlignment.CENTER
      ? 'center'
      : textAlignment === TextAlignment.RIGHT
      ? 'flex-end'
      : 'flex-start'};
  align-self: ${({ isChapter }) => (isChapter ? 'center' : 'flex-start')};
  margin: 0;
  padding: 0;
  overflow: visible;

  > * {
    height: auto !important;
    min-height: 0;
    overflow: visible !important;
    max-height: none !important;
  }
`;

type SlideTextImageTemplateViewerProps = {
  presentationId?: string;
  onPreviousModule: () => void;
  onLastPageReached?: () => void;
  continueOrFinishButton?: React.ReactNode;
  accessibilityIcon?: React.ReactNode;
  courseId?: string;
  attemptLanguage?: LanguagesEnum;
  rtl?: boolean;
  hasNextModule?: boolean;
  hasPreviousModule?: boolean;
  isAuthoringPreview?: boolean;
};

export const SlideTextImageTemplateViewer: React.FC<
  SlideTextImageTemplateViewerProps
> = ({
  presentationId,
  onPreviousModule,
  onLastPageReached,
  continueOrFinishButton,
  accessibilityIcon,
  courseId,
  attemptLanguage = LanguagesEnum.EN,
  rtl,
  hasNextModule,
  hasPreviousModule,
  isAuthoringPreview,
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const [currentSlideIdx, setCurrentSlideIdx] = useState(0);
  const mainContentRef = useRef<HTMLDivElement>(null);
  const [shadowTop, setShadowTop] = useState(false);
  const [shadowBottom, setShadowBottom] = useState(false);

  const currentAccount = useSelector(selectCurrentAccount);
  const { canUseFeature } = useFeatureFlag();
  const variablesEnabled = canUseFeature(
    PostHogFeatureFlags.TRAINING_VARIABLES,
  );
  const { variables: accountVariables } = useAccountVariables(
    currentAccount?.id,
    { enabled: variablesEnabled },
  );

  const resolvedVariables = useMemo(
    () =>
      isAuthoringPreview && currentAccount?.isSystem
        ? withExamplePointOfContact(accountVariables, t)
        : accountVariables,
    [accountVariables, isAuthoringPreview, currentAccount?.isSystem, t],
  );

  const updateScrollShadows = useCallback(() => {
    const el = mainContentRef.current;
    if (!el) return;
    setShadowTop(el.scrollTop > 0);
    setShadowBottom(el.scrollTop + el.clientHeight < el.scrollHeight - 1);
  }, []);

  const { slideDeck, isLoading } = useSlideDeck({
    courseId,
    presentationId,
    onError: (e) => handleAxiosError(e as Error | AxiosError, toast),
  });

  const slides = useMemo(() => {
    const currentTranslation = slideDeck?.translations?.find(
      (translation) => translation.language === attemptLanguage,
    );

    const slidesForLanguage = currentTranslation?.slides ?? [];

    const sortByPosition = (a: Slide, b: Slide) =>
      (a.position || 0) - (b.position || 0);

    return [...slidesForLanguage].sort(sortByPosition);
  }, [slideDeck, attemptLanguage]);

  const normalSlides = slides.filter(
    (slide) => slide.type === SlideType.NORMAL,
  );
  const currentSlideData = normalSlides[currentSlideIdx] || {};
  const { targetRef: slideHeadingRef } = useTransitionFocus<HTMLElement>(
    currentSlideData.id,
  );

  const effectiveTextAlignment =
    rtl && currentSlideData.textAlignment !== TextAlignment.CENTER
      ? TextAlignment.RIGHT
      : currentSlideData.textAlignment ?? TextAlignment.LEFT;

  const handlePrevSlide = () => {
    if (currentSlideIdx > 0) {
      const newIndex = currentSlideIdx - 1;
      setCurrentSlideIdx(newIndex);
    } else {
      onPreviousModule();
    }
  };

  const handleNextSlide = () => {
    if (currentSlideIdx < normalSlides.length - 1) {
      const newIndex = currentSlideIdx + 1;
      setCurrentSlideIdx(newIndex);
    }
  };

  useEffect(() => {
    if (currentSlideIdx >= normalSlides.length && normalSlides.length > 0) {
      setCurrentSlideIdx(normalSlides.length - 1);
    }
  }, [normalSlides.length, currentSlideIdx]);

  useEffect(() => {
    const el = mainContentRef.current;
    if (!el) return;
    updateScrollShadows();
    el.addEventListener('scroll', updateScrollShadows);
    return () => el.removeEventListener('scroll', updateScrollShadows);
  }, [currentSlideIdx, updateScrollShadows]);

  if (isLoading) {
    return (
      <ViewerContainer isMobile={isMobile()} dir={rtl ? 'rtl' : 'ltr'}>
        <MainContent
          style={{
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <ProgressSpinner />
        </MainContent>
      </ViewerContainer>
    );
  }

  const isPurpleBackground =
    currentSlideData.templateType === SlideTemplateType.SUMMARY;
  const isChapter = currentSlideData.templateType === SlideTemplateType.CHAPTER;

  return (
    <ViewerContainer
      isMobile={isMobile()}
      isPurpleBackground={isPurpleBackground}
      dir={rtl ? 'rtl' : 'ltr'}
    >
      {accessibilityIcon}
      <ScrollShadowWrapper>
        <ScrollShadow $position="top" $visible={shadowTop} />
        <ScrollShadow $position="bottom" $visible={shadowBottom} />
        <MainContent
          as="article"
          ref={mainContentRef}
          isPurpleBackground={isPurpleBackground}
        >
          <ContentColumn
            isMobile={isMobile()}
            isChapter={isChapter}
            hasAccessibilityIcon={!!accessibilityIcon}
          >
            <ProgressHeader
              currentIndex={currentSlideIdx}
              totalCount={normalSlides.length}
              attemptLanguage={attemptLanguage}
              type="slide"
            />
            <SlideContentContainer
              gap={22}
              justify="space-between"
              align={isChapter ? 'center' : 'flex-start'}
              height={isChapter ? '100%' : 'auto'}
              flex={isChapter ? '1' : '0'}
              isMobile={isMobile()}
              textAlignment={effectiveTextAlignment}
            >
              <FlexContainer
                direction="column"
                height={isChapter ? '100%' : 'auto'}
                justify={isChapter ? 'center' : 'flex-start'}
                align={
                  effectiveTextAlignment === TextAlignment.CENTER
                    ? 'center'
                    : effectiveTextAlignment === TextAlignment.RIGHT
                    ? 'flex-end'
                    : 'flex-start'
                }
              >
                <RichTextRenderer
                  isTitle
                  ref={slideHeadingRef}
                  tabIndex={-1}
                  content={
                    currentSlideData.headline || t('content.slide.title')
                  }
                  textAlignment={effectiveTextAlignment}
                  rtl={rtl}
                  accountVariables={resolvedVariables}
                />

                <RichTextRenderer
                  content={currentSlideData.html || ''}
                  className={isPurpleBackground ? 'rich-text-summary' : ''}
                  textAlignment={effectiveTextAlignment}
                  rtl={rtl}
                  accountVariables={resolvedVariables}
                />

                {currentSlideData.infobox &&
                  [
                    SlideTemplateType.TEXT_IMAGE,
                    SlideTemplateType.SUMMARY,
                  ].includes(currentSlideData.templateType) && (
                    <InfoBox
                      text={currentSlideData.infobox}
                      modalContent={getPopupSlideContent(
                        currentSlideData,
                        slides,
                      )}
                      isSummary={isPurpleBackground}
                      rtl={rtl}
                      attemptLanguage={attemptLanguage}
                    />
                  )}
              </FlexContainer>

              {currentSlideData.interactiveElement?.type ===
                InteractiveElementType.EMAIL && (
                <ContentEmailComponent
                  key={currentSlideData.id}
                  id={currentSlideData.id}
                  interactiveElement={currentSlideData.interactiveElement}
                  readOnly={true}
                />
              )}

              {currentSlideData.interactiveElement?.type ===
                InteractiveElementType.SMS && (
                <ContentSMSComponent
                  key={currentSlideData.id}
                  id={currentSlideData.id}
                  interactiveElement={currentSlideData.interactiveElement}
                  readOnly={true}
                  rtl={rtl}
                />
              )}

              {!currentSlideData.interactiveElement &&
                currentSlideData.images &&
                currentSlideData.images.length > 0 &&
                [
                  SlideTemplateType.TEXT_IMAGE,
                  SlideTemplateType.CHAPTER,
                  SlideTemplateType.SUMMARY,
                ].includes(currentSlideData.templateType) && (
                  <ImageGridContainer
                    isChapter={isChapter}
                    textAlignment={effectiveTextAlignment}
                    isMobile={isMobile()}
                  >
                    {currentSlideData.images.map((image, index) => (
                      <SlideImageComponent
                        key={index}
                        image={image}
                        currentEditingSlide={currentSlideData}
                        textAlignment={effectiveTextAlignment}
                        isSummary={
                          currentSlideData.templateType ===
                          SlideTemplateType.SUMMARY
                        }
                      />
                    ))}
                  </ImageGridContainer>
                )}
            </SlideContentContainer>
          </ContentColumn>
          {currentSlideData.images &&
            currentSlideData.images.length > 0 &&
            SlideTemplateType.GALLERY === currentSlideData.templateType && (
              <ContentColumn isMobile={isMobile()}>
                <ImageGridContainer
                  textAlignment={effectiveTextAlignment}
                  isGallery={true}
                  isMobile={isMobile()}
                  style={{ marginTop: '16px' }}
                >
                  {currentSlideData.images.map((image, index) => (
                    <SlideImageComponent
                      key={index}
                      image={image}
                      currentEditingSlide={currentSlideData}
                      textAlignment={effectiveTextAlignment}
                      isGallery={true}
                    />
                  ))}
                </ImageGridContainer>
              </ContentColumn>
            )}
          {currentSlideData.footnote &&
            [SlideTemplateType.TEXT_IMAGE, SlideTemplateType.SUMMARY].includes(
              currentSlideData.templateType,
            ) && (
              <ContentColumn padding="no-top" isMobile={isMobile()}>
                <Footnote>{currentSlideData.footnote}</Footnote>
              </ContentColumn>
            )}
        </MainContent>
      </ScrollShadowWrapper>

      <SlidesNavigationContainer isPurpleBackground={isPurpleBackground}>
        <ContentColumn padding="none" isMobile={isMobile()}>
          <SlidesNavigation
            currentSlideIdx={currentSlideIdx}
            totalSlides={normalSlides.length}
            isEditingPopUpSlide={false}
            hasNextModule={hasNextModule}
            hasPreviousModule={hasPreviousModule}
            attemptLanguage={attemptLanguage}
            rtl={rtl}
            onPrevSlide={handlePrevSlide}
            onNextSlide={handleNextSlide}
            onLastPageReached={onLastPageReached}
            continueOrFinishButton={continueOrFinishButton}
          />
        </ContentColumn>
      </SlidesNavigationContainer>
    </ViewerContainer>
  );
};
